import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { MessageSquare, Send, ArrowLeft, User, Image as ImageIcon, Link as LinkIcon, Check, Mail, MessageCircle, Trash2 } from 'lucide-react';
import { collection, addDoc, query, where, onSnapshot, orderBy, updateDoc, doc, getDocs, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';

const Messaging = () => {
  const { currentUser, userProfile } = useAuth();
  const [clients, setClients] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showSendOptionsModal, setShowSendOptionsModal] = useState(false);
  const [lastSentMessage, setLastSentMessage] = useState('');
  const messagesEndRef = useRef(null);

  // Get client info from conversation
  const getClientInfo = () => {
    if (!selectedConversation || !clients.length) return null;
    
    const clientId = selectedConversation.participantIds.find(id => id !== currentUser.uid);
    return clients.find(c => c.id === clientId);
  };

  // Create mailto link
  const sendViaEmail = () => {
    const clientInfo = getClientInfo();
    if (!clientInfo || !clientInfo.email) {
      alert('No email address found for this client. Please add their email in the Clients page.');
      return;
    }

    const replyLink = `${window.location.origin}/reply/${selectedConversation.id}`;
    const businessName = userProfile?.businessName || userProfile?.displayName || 'PetPages';
    const clientName = clientInfo.name || `${clientInfo.firstName || ''} ${clientInfo.lastName || ''}`.trim();

    const subject = encodeURIComponent(`Message from ${businessName} - PetPages`);
    const body = encodeURIComponent(
`Hi ${clientName}!

I wanted to reach out about your pet care.

${lastSentMessage ? `Message:\n${lastSentMessage}\n\n` : ''}You can reply to this message here:
${replyLink}

Thanks!
${businessName}

---
Sent via PetPages`
    );

    window.location.href = `mailto:${clientInfo.email}?subject=${subject}&body=${body}`;
    setShowSendOptionsModal(false);
  };

  // Create SMS link
  const sendViaSMS = () => {
    const clientInfo = getClientInfo();
    if (!clientInfo || !clientInfo.phone) {
      alert('No phone number found for this client. Please add their phone number in the Clients page.');
      return;
    }

    const replyLink = `${window.location.origin}/reply/${selectedConversation.id}`;
    const businessName = userProfile?.businessName || userProfile?.displayName || 'PetPages';
    const clientName = clientInfo.name || `${clientInfo.firstName || ''} ${clientInfo.lastName || ''}`.trim();

    // Format phone number (remove non-digits)
    const phoneNumber = clientInfo.phone.replace(/\D/g, '');
    
    const messageBody = encodeURIComponent(
`Hi ${clientName}! Message from ${businessName}:

${lastSentMessage}

Reply here: ${replyLink}`
    );

    // Use sms: protocol (works on both iOS and Android)
    // iOS uses &, Android uses ?
    const separator = /iPhone|iPad|iPod/i.test(navigator.userAgent) ? '&' : '?';
    window.location.href = `sms:${phoneNumber}${separator}body=${messageBody}`;
    setShowSendOptionsModal(false);
  };

  const copyReplyLink = () => {
    if (!selectedConversation) return;
    
    const replyLink = `${window.location.origin}/reply/${selectedConversation.id}`;
    navigator.clipboard.writeText(replyLink).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy link:', err);
      alert('Failed to copy link');
    });
  };

  // Delete conversation
  const handleDeleteConversation = async (conversationId, e) => {
    e.stopPropagation(); // Prevent selecting the conversation
    
    try {
      // Delete the conversation document
      await deleteDoc(doc(db, 'conversations', conversationId));
      
      // If this was the selected conversation, clear selection
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
      }
      
      alert('✅ Chat deleted successfully!');
    } catch (error) {
      console.error('Error deleting conversation:', error);
      alert('❌ Failed to delete chat. Please try again.');
    }
  };

  // Fetch clients
  useEffect(() => {
    const fetchClients = async () => {
      if (!currentUser) return;

      try {
        const clientsRef = collection(db, 'clients');
        const q = query(clientsRef, where('sitterId', '==', currentUser.uid));
        const snapshot = await getDocs(q);
        const clientsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setClients(clientsData);
      } catch (error) {
        console.error('Error fetching clients:', error);
      }
    };

    fetchClients();
  }, [currentUser]);

  // Real-time listener for conversations
  useEffect(() => {
    if (!currentUser) return;

    const conversationsRef = collection(db, 'conversations');
    const q = query(
      conversationsRef,
      where('participantIds', 'array-contains', currentUser.uid)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const conversationsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      // Sort by last message time
      conversationsData.sort((a, b) => {
        const aTime = a.lastMessageAt?.toMillis() || 0;
        const bTime = b.lastMessageAt?.toMillis() || 0;
        return bTime - aTime;
      });
      
      setConversations(conversationsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Real-time listener for messages in selected conversation
  useEffect(() => {
    if (!selectedConversation) {
      setMessages([]);
      return;
    }

    const messagesRef = collection(db, 'conversations', selectedConversation.id, 'messages');
    const q = query(messagesRef, orderBy('createdAt', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messagesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setMessages(messagesData);

      // Mark messages as read
      messagesData.forEach(async (msg) => {
        if (msg.senderId !== currentUser.uid && !msg.read) {
          const messageRef = doc(db, 'conversations', selectedConversation.id, 'messages', msg.id);
          await updateDoc(messageRef, { read: true });
        }
      });
    });

    return () => unsubscribe();
  }, [selectedConversation, currentUser]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const startConversation = async (client) => {
    // Check if conversation already exists
    const existing = conversations.find(conv => 
      conv.participantIds.includes(client.id)
    );

    if (existing) {
      setSelectedConversation(existing);
      return;
    }

    // Create new conversation
    try {
      const conversationsRef = collection(db, 'conversations');
      const newConv = await addDoc(conversationsRef, {
        participantIds: [currentUser.uid, client.id],
        participantNames: {
          [currentUser.uid]: userProfile?.displayName || currentUser.email,
          [client.id]: client.name,
        },
        participantTypes: {
          [currentUser.uid]: 'sitter',
          [client.id]: 'client',
        },
        lastMessage: '',
        lastMessageAt: serverTimestamp(),
        unreadCount: {
          [currentUser.uid]: 0,
          [client.id]: 0,
        },
      });

      setSelectedConversation({
        id: newConv.id,
        participantIds: [currentUser.uid, client.id],
        participantNames: {
          [currentUser.uid]: userProfile?.displayName || currentUser.email,
          [client.id]: client.name,
        },
      });
    } catch (error) {
      console.error('Error creating conversation:', error);
      alert('Failed to start conversation');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    setSendingMessage(true);
    try {
      const messagesRef = collection(db, 'conversations', selectedConversation.id, 'messages');
      await addDoc(messagesRef, {
        text: newMessage,
        senderId: currentUser.uid,
        senderName: userProfile?.displayName || currentUser.email,
        createdAt: serverTimestamp(),
        read: false,
      });

      // Update conversation last message
      const conversationRef = doc(db, 'conversations', selectedConversation.id);
      await updateDoc(conversationRef, {
        lastMessage: newMessage.substring(0, 100),
        lastMessageAt: serverTimestamp(),
      });

      // Store message and show send options modal
      setLastSentMessage(newMessage);
      setNewMessage('');
      setShowSendOptionsModal(true);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const sendImage = async (file) => {
    if (!file || !selectedConversation) return;

    setUploadingImage(true);
    try {
      // Upload image to storage
      const fileName = `message-images/${selectedConversation.id}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // Send message with image
      const messagesRef = collection(db, 'conversations', selectedConversation.id, 'messages');
      await addDoc(messagesRef, {
        text: '',
        imageUrl: downloadURL,
        senderId: currentUser.uid,
        senderName: userProfile?.displayName || currentUser.email,
        createdAt: serverTimestamp(),
        read: false,
      });

      // Update conversation
      const conversationRef = doc(db, 'conversations', selectedConversation.id);
      await updateDoc(conversationRef, {
        lastMessage: '📷 Sent an image',
        lastMessageAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error sending image:', error);
      alert('Failed to send image');
    } finally {
      setUploadingImage(false);
    }
  };

  const getOtherParticipantName = (conversation) => {
    const otherUserId = conversation.participantIds.find(id => id !== currentUser.uid);
    return conversation.participantNames?.[otherUserId] || 'Unknown';
  };

  const getUnreadCount = (conversation) => {
    return conversation.unreadCount?.[currentUser.uid] || 0;
  };

  return (
    <Layout>
      <div className="flex h-[calc(100vh-120px)]">
        {/* Conversations List */}
        <div className={`
          ${selectedConversation ? 'hidden md:block' : 'block'}
          w-full md:w-1/3 border-r border-brand-brown/10 overflow-y-auto
        `}>
          <div className="p-6 border-b border-brand-brown/10">
            <div className="flex items-center space-x-3 mb-4">
              <MessageSquare className="h-8 w-8 text-pastel-purple" />
              <h1 className="text-2xl font-bold text-brand-brown">Messages</h1>
            </div>

            {/* New Conversation */}
            <div>
              <h3 className="text-sm font-medium text-brand-brown/70 mb-2">Start New Chat</h3>
              <select
                onChange={(e) => {
                  const client = clients.find(c => c.id === e.target.value);
                  if (client) startConversation(client);
                  e.target.value = '';
                }}
                className="input text-sm"
              >
                <option value="">Select a client...</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Conversation List */}
          <div>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pastel-purple"></div>
              </div>
            ) : conversations.length === 0 ? (
              <div className="p-8 text-center">
                <MessageSquare className="h-12 w-12 text-brand-brown/30 mx-auto mb-3" />
                <p className="text-brand-brown/60">No conversations yet</p>
                <p className="text-sm text-brand-brown/50 mt-1">Select a client above to start chatting</p>
              </div>
            ) : (
              conversations.map(conversation => (
                <div
                  key={conversation.id}
                  className={`
                    w-full relative border-b border-brand-brown/10 hover:bg-pastel-pink/20 transition-colors
                    ${selectedConversation?.id === conversation.id ? 'bg-pastel-pink/30' : ''}
                  `}
                >
                  <button
                    onClick={() => setSelectedConversation(conversation)}
                    className="w-full p-4 text-left"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-brand-brown pr-8">
                        {getOtherParticipantName(conversation)}
                      </h3>
                      {conversation.lastMessageAt && (
                        <span className="text-xs text-brand-brown/50">
                          {format(conversation.lastMessageAt.toDate(), 'MMM d')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-brand-brown/60 truncate flex-1 pr-8">
                        {conversation.lastMessage || 'No messages yet'}
                      </p>
                      {getUnreadCount(conversation) > 0 && (
                        <span className="ml-2 bg-pastel-purple text-white text-xs rounded-full px-2 py-1">
                          {getUnreadCount(conversation)}
                        </span>
                      )}
                    </div>
                  </button>
                  
                  {/* Delete Button */}
                  <button
                    onClick={(e) => handleDeleteConversation(conversation.id, e)}
                    className="absolute top-4 right-4 p-2 hover:bg-red-50 rounded-lg text-red-500 transition-colors"
                    title="Delete chat"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Messages View */}
        <div className={`
          ${selectedConversation ? 'block' : 'hidden md:block'}
          flex-1 flex flex-col
        `}>
          {selectedConversation ? (
            <>
              {/* Header */}
              <div className="p-4 border-b border-brand-brown/10 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setSelectedConversation(null)}
                    className="md:hidden p-2 hover:bg-pastel-pink/20 rounded-lg"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <User className="h-8 w-8 text-pastel-purple" />
                  <h2 className="text-xl font-bold text-brand-brown">
                    {getOtherParticipantName(selectedConversation)}
                  </h2>
                </div>
                
                {/* Copy Reply Link Button */}
                <button
                  onClick={copyReplyLink}
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-pastel-blue/20 text-brand-brown transition-colors"
                  title="Copy link for client to reply"
                >
                  {linkCopied ? (
                    <>
                      <Check className="h-4 w-4 text-green-600" />
                      <span className="text-xs font-medium text-green-600 hidden sm:inline">Copied!</span>
                    </>
                  ) : (
                    <>
                      <LinkIcon className="h-4 w-4" />
                      <span className="text-xs font-medium hidden sm:inline">Copy Reply Link</span>
                    </>
                  )}
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map(message => (
                  <div
                    key={message.id}
                    className={`flex ${message.senderId === currentUser.uid ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[70%] ${message.senderId === currentUser.uid ? 'order-2' : 'order-1'}`}>
                      <div className={`
                        rounded-2xl p-3 ${
                          message.senderId === currentUser.uid
                            ? 'bg-pastel-purple text-white'
                            : 'bg-gray-100 text-brand-brown'
                        }
                      `}>
                        {message.imageUrl && (
                          <img
                            src={message.imageUrl}
                            alt="Shared"
                            className="rounded-lg mb-2 max-w-full"
                          />
                        )}
                        {message.text && <p className="break-words">{message.text}</p>}
                      </div>
                      <p className={`text-xs text-brand-brown/50 mt-1 ${
                        message.senderId === currentUser.uid ? 'text-right' : 'text-left'
                      }`}>
                        {message.createdAt && format(message.createdAt.toDate(), 'h:mm a')}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <form onSubmit={sendMessage} className="p-4 border-t border-brand-brown/10">
                <div className="flex items-center space-x-2">
                  <label className="cursor-pointer p-2 hover:bg-pastel-pink/20 rounded-lg transition-colors">
                    <ImageIcon className="h-5 w-5 text-brand-brown/60" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) sendImage(file);
                      }}
                      className="hidden"
                      disabled={uploadingImage}
                    />
                  </label>
                  
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 px-4 py-2 rounded-xl border-2 border-brand-brown/20 focus:border-pastel-purple focus:outline-none"
                    disabled={sendingMessage || uploadingImage}
                  />
                  
                  <button
                    type="submit"
                    disabled={!newMessage.trim() || sendingMessage || uploadingImage}
                    className="btn-primary px-4 py-2 disabled:opacity-50"
                  >
                    {uploadingImage ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <Send className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="h-20 w-20 text-brand-brown/20 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-brand-brown mb-2">
                  Select a conversation
                </h3>
                <p className="text-brand-brown/60">
                  Choose a client to start messaging
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Send Options Modal */}
      {showSendOptionsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl animate-scale-in">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-pastel-blue to-pastel-purple rounded-full flex items-center justify-center mx-auto mb-4">
                <Send className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-brand-brown mb-2">
                Message Saved!
              </h3>
              <p className="text-brand-brown/60 text-sm">
                How would you like to notify the client?
              </p>
            </div>

            <div className="space-y-3">
              {/* Email Button */}
              <button
                onClick={sendViaEmail}
                className="w-full flex items-center justify-center space-x-3 px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl"
              >
                <Mail className="h-6 w-6" />
                <span className="font-semibold text-lg">Send via Email</span>
              </button>

              {/* SMS Button */}
              <button
                onClick={sendViaSMS}
                className="w-full flex items-center justify-center space-x-3 px-6 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 transition-all shadow-lg hover:shadow-xl"
              >
                <MessageCircle className="h-6 w-6" />
                <span className="font-semibold text-lg">Send via SMS</span>
              </button>

              {/* Manual Send Button */}
              <button
                onClick={() => {
                  copyReplyLink();
                  setShowSendOptionsModal(false);
                }}
                className="w-full flex items-center justify-center space-x-3 px-6 py-4 bg-pastel-purple/20 text-brand-brown rounded-xl hover:bg-pastel-purple/30 transition-colors border-2 border-pastel-purple/30"
              >
                <LinkIcon className="h-5 w-5" />
                <span className="font-medium">Copy Link (Send Manually)</span>
              </button>

              {/* Close Button */}
              <button
                onClick={() => setShowSendOptionsModal(false)}
                className="w-full px-6 py-3 text-brand-brown/60 hover:text-brand-brown transition-colors text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Messaging;
